import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";

// Release 1.5.0 offline read: whether the phone has a connection (the browser's own online/offline signal), since
// when it has been offline, and the time of the newest figures the open page is showing ("Offline — showing data from
// 14:32"). Nothing here stores anything.

let online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
let offlineSince: number | null = online ? null : Date.now();
const listeners = new Set<() => void>();

function set(next: boolean): void {
  if (next === online) return;
  online = next;
  offlineSince = next ? null : Date.now();
  listeners.forEach((l) => l());
}

let installed = false;
function install(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("online", () => set(true));
  window.addEventListener("offline", () => set(false));
}

function subscribe(l: () => void): () => void {
  install();
  listeners.add(l);
  return () => listeners.delete(l);
}

export function isOnline(): boolean {
  install();
  return online;
}

export function getOfflineSince(): number | null {
  return offlineSince;
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, () => online, () => true);
}

/** Test helper: pretend the connection changed. */
export function setOnlineForTests(value: boolean): void {
  install();
  set(value);
}

/** HH:MM on the phone's clock, 24-hour (what the person sees on the wall clock). */
export function formatClock(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Query roots that are not page figures (header chips, bell, release info): left out of "showing data from". */
const NOT_PAGE_DATA = new Set(["header", "notification-centre", "notifications", "releases", "live", "phone"]);

/**
 * When the figures on screen were fetched: the OLDEST successful answer among the queries the open page is using (those
 * with an observer), so the banner never claims fresher data than the page shows; null when nothing on screen has data.
 */
export function shownDataAt(queryClient: QueryClient): number | null {
  let oldest: number | null = null;
  for (const q of queryClient.getQueryCache().getAll()) {
    if (q.getObserversCount() === 0 || q.state.dataUpdatedAt === 0 || q.state.data === undefined) continue;
    const root = q.queryKey[0];
    if (typeof root === "string" && NOT_PAGE_DATA.has(root)) continue;
    if (oldest === null || q.state.dataUpdatedAt < oldest) oldest = q.state.dataUpdatedAt;
  }
  return oldest;
}

/** The banner's words. */
export function offlineBannerText(dataAt: number | null, since: number | null): string {
  if (dataAt !== null) return `Offline — showing data from ${formatClock(dataAt)}`;
  if (since !== null) return `Offline since ${formatClock(since)} — this page was not opened before the connection dropped`;
  return "Offline";
}
